import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { QRCodeSVG } from 'qrcode.react';
import { Mountain, Compass, Flame, Users, Copy, RotateCw, TicketPercent, ScanLine } from 'lucide-react';

/**
 * Tessera Sila — card premium wallet con FLIP 3D fronte/retro.
 * Livelli (la LOGICA punti/sconti resta invariata, cambiano nomi e palette):
 *   Conoscenza → Passione → Connessione.
 * Palette derivata dal sito + logo (lago / calore / pineta). Sagoma bosco+monti della Sila.
 * Contenuti in unita' di container (cqw): scalano con la card. Il QR e' REALE.
 */

const TIERS = {
  bronze: { label: 'Conoscenza', Icon: Compass, discount: 5 },
  silver: { label: 'Passione', Icon: Flame, discount: 8 },
  gold: { label: 'Connessione', Icon: Users, discount: 12 },
};

// Sagoma "paesaggio Sila": crinale dei monti + pineta + luna (niente lupo).
// Bianco a bassa opacita' => visibile su qualsiasi colore di livello (watermark).
const SilaScape = () => (
  <svg
    aria-hidden
    viewBox="0 0 320 202"
    preserveAspectRatio="xMidYMid slice"
    className="pointer-events-none absolute inset-0 h-full w-full"
  >
    {/* Luna (richiamo al logo) */}
    <circle cx="238" cy="60" r="26" fill="#fff" opacity="0.12" />
    {/* Crinale dei monti */}
    <path d="M0 202 V168 L40 150 L78 166 L120 140 L165 162 L215 134 L262 158 L320 142 V202 Z"
          fill="#fff" opacity="0.12" />
    {/* Pineta in primo piano */}
    <g fill="#fff" opacity="0.22">
      <path d="M0 202 V190 H320 V202 Z" />
      <path d="M22 190 L38 150 L54 190 Z M30 190 L38 166 L46 190 Z" />
      <path d="M60 190 L78 142 L96 190 Z M69 190 L78 162 L87 190 Z" />
      <path d="M300 190 L286 152 L272 190 Z" />
      <path d="M262 190 L250 160 L238 190 Z" />
      <path d="M112 190 L124 60 L136 190 Z M112 190 L124 60 L136 190 Z" />
      <path d="M150 190 L164 156 L178 190 Z" />
    </g>
  </svg>
);

const ico = (cqw) => ({ width: `${cqw}cqw`, height: `${cqw}cqw` });

const QrCode = ({ value, sizeCqw }) => (
  <QRCodeSVG
    value={value || 'SILA'}
    size={128}
    level="M"
    marginSize={0}
    bgColor="transparent"
    fgColor="#0b1c14"
    title="QR Tessera Sila"
    role="img"
    aria-label="QR Tessera Sila"
    style={{ width: `${sizeCqw}cqw`, height: `${sizeCqw}cqw`, display: 'block' }}
  />
);

export const SilaWalletCard = ({
  tier = 'gold',
  name = 'Cliente Sila',
  cardNumber = 'SILA-0000-0000',
  points = 0,
  onCopy,
  className,
  shimmer = true,
  interactive = true,
  discountPct = null,
  title = 'Tessera Sila',
}) => {
  const t = TIERS[tier] || TIERS.gold;
  const TierIcon = t.Icon;
  const disc = discountPct != null ? discountPct : t.discount;
  const [flipped, setFlipped] = useState(false);

  const TierBadge = (
    <span className="wc-badge" data-testid="wallet-tier-badge" style={{ color: '#fff' }}>
      <TierIcon style={{ ...ico(3.4), color: 'var(--tier-base)' }} />{t.label}
    </span>
  );

  return (
    <div
      data-tier={tier}
      data-testid="wallet-sila-card"
      className={cn('sila-flip w-full max-w-xl aspect-[1.586/1] text-white', flipped && 'is-flipped', className)}
    >
      <div className="sila-flip-viewport">
        <div className="sila-flip-inner">
        {/* ============ FRONTE ============ */}
        <div className="sila-flip-face sila-wallet-card">
          {shimmer && <span aria-hidden className="sila-shimmer" />}
          <span aria-hidden className="sila-wallet-topo" />
          <SilaScape />
          <div className="wc">
            <div className="wc-top">
              <div className="wc-brand">
                <span className="wc-brandbox"><Mountain style={ico(5.4)} /></span>
                <div>
                  <p className="wc-brand-sm">Sila Experiences</p>
                  <p className="wc-title" data-testid="wallet-tier-title">{t.label}</p>
                </div>
              </div>
              <div className="wc-actions">
                {interactive && (
                  <button
                    type="button"
                    onClick={() => setFlipped((v) => !v)}
                    aria-label="Gira la tessera"
                    title="Gira la tessera"
                    data-testid="wallet-flip-button"
                    className="wc-flipbtn"
                  >
                    <RotateCw style={ico(4)} />
                  </button>
                )}
                {TierBadge}
              </div>
            </div>

            <div className="wc-mid">
              <p className="wc-label">Punti disponibili</p>
              <p className="wc-points" data-testid="wallet-points-balance">{Number(points).toLocaleString('it-IT')}</p>
            </div>

            <div className="wc-bottom">
              <div style={{ minWidth: 0 }}>
                <p className="wc-label">Titolare</p>
                <p className="wc-name" data-testid="wallet-customer-name">{name}</p>
                {interactive ? (
                  <button type="button" onClick={onCopy} className="wc-num" data-testid="wallet-card-number" aria-label="Copia numero tessera">
                    {cardNumber}<Copy style={ico(2.8)} />
                  </button>
                ) : (
                  <span className="wc-num wc-num--static" data-testid="wallet-card-number">{cardNumber}</span>
                )}
              </div>
              <div className="wc-qr" role="img" aria-label="QR Tessera Sila">
                <QrCode value={cardNumber} sizeCqw={16.5} />
              </div>
            </div>
          </div>
        </div>

        {/* ============ RETRO ============ */}
        <div className="sila-flip-face sila-flip-back sila-wallet-card">
          <span aria-hidden className="sila-wallet-topo" />
          <div className="wc">
            <div className="wc-stripe" aria-hidden />
            <div className="wc-back-body">
              <div className="wc-qr">
                <QrCode value={cardNumber} sizeCqw={28} />
              </div>
              <div className="wc-back-text">
                <p className="wc-back-title"><ScanLine style={{ ...ico(3.8), color: 'var(--tier-base)' }} />Scansiona per accumulare</p>
                <p className="wc-back-desc">Mostra questo QR alle casse dei partner Sila per accumulare punti e applicare i vantaggi della tua Tessera.</p>
                <p className="wc-back-disc"><TicketPercent style={{ ...ico(3.2), color: 'var(--tier-base)' }} />Sconto attuale <strong style={{ color: 'var(--tier-base)' }}>{disc}%</strong></p>
              </div>
            </div>
            <div className="wc-bottom">
              <div style={{ minWidth: 0 }}>
                <p className="wc-label">Numero tessera</p>
                <span className="wc-num wc-num--static">{cardNumber}</span>
              </div>
              {TierBadge}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default SilaWalletCard;
